import React, { useState, useEffect } from "react";
import axios from "axios";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar, Legend } from "recharts";
import { API_URL, getAuthConfig } from "./shared";

/*
 * Laboratoire — Statistiques (cahier des charges labo, §21, "Niveau site"),
 * DEDIE.
 *
 * Port de LabStatsModule (HopitalDashboard.js), repointe sur
 * /api/lab/reception/stats/trend au lieu de /api/sih/reception/stats/trend.
 * Aucun changement backend necessaire : cette route existe deja depuis le
 * fork de backend/routes/lab/reception.py (PR3b) et renvoie exactement la
 * meme forme de reponse ({summary, trend}) que l'original hopital.
 */
export default function LabStatsModule({ can }) {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    setLoading(true); setError("");
    axios.get(`${API_URL}/api/lab/reception/stats/trend`, { ...getAuthConfig(), params: { days } })
      .then((r) => setData(r.data))
      .catch(() => setError("Impossible de charger les statistiques"))
      .finally(() => setLoading(false));
  }, [days]);

  const Kpi = ({ label, value, sub, color }) => (
    <div style={{ background: "#fff", border: "1px solid #E2E8F0", borderRadius: 12, padding: "16px 18px" }}>
      <div style={{ fontSize: 12, color: "#64748B", marginBottom: 6 }}>{label}</div>
      <div style={{ fontSize: 28, fontWeight: 700, color: color || "#0F172A", lineHeight: 1 }}>{value}</div>
      {sub && <div style={{ fontSize: 12, color: "#94A3B8", marginTop: 6 }}>{sub}</div>}
    </div>
  );
  const Panel = ({ title, children }) => (
    <div style={{ background: "#fff", border: "1px solid #E2E8F0", borderRadius: 12, padding: 18 }}>
      <div style={{ fontSize: 14, fontWeight: 600, color: "#334155", marginBottom: 14 }}>{title}</div>
      {children}
    </div>
  );

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: "#0F172A", margin: 0 }}>Statistiques</h1>
          <div style={{ fontSize: 13, color: "#94A3B8", marginTop: 4 }}>Indicateurs du laboratoire sur la periode selectionnee</div>
        </div>
        <select value={days} onChange={(e) => setDays(Number(e.target.value))} style={{ padding: "9px 12px", border: "1px solid #CBD5E1", borderRadius: 8, fontSize: 14, background: "#fff" }}>
          <option value={7}>7 derniers jours</option>
          <option value={30}>30 derniers jours</option>
          <option value={90}>90 derniers jours</option>
        </select>
      </div>

      {loading && <div style={{ marginTop: 20, color: "#94A3B8", fontSize: 14 }}>Chargement des indicateurs...</div>}
      {error && <div style={{ background: "#FCEBEB", border: "1px solid #F7C1C1", color: "#A32D2D", borderRadius: 8, padding: "10px 14px", marginTop: 14, fontSize: 13 }}>{error}</div>}

      {!loading && data && (
        <>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))", gap: 14, marginTop: 18 }}>
            <Kpi label="Patients accueillis" value={data.summary.patients_arrives} color="#185FA5" />
            <Kpi label="Rendez-vous" value={data.summary.rendez_vous} color="#0596DE" />
            <Kpi label="Annulations" value={data.summary.annulations} sub={data.summary.taux_annulation_pct != null ? `${data.summary.taux_annulation_pct}% des RDV` : "—"} color="#854F0B" />
            <Kpi label="No-show" value={data.summary.no_show} sub={data.summary.taux_no_show_pct != null ? `${data.summary.taux_no_show_pct}% des RDV honores` : "—"} color="#A32D2D" />
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginTop: 22 }}>
            <Panel title="Patients accueillis / rendez-vous par jour">
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={data.trend} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
                  <XAxis dataKey="day" tick={{ fontSize: 10 }} />
                  <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                  <Tooltip />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Bar dataKey="patients_arrives" name="Patients" fill="#185FA5" radius={[3, 3, 0, 0]} />
                  <Bar dataKey="rendez_vous" name="Rendez-vous" fill="#0596DE" radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </Panel>
            <Panel title="Taux d'occupation (%)">
              <ResponsiveContainer width="100%" height={220}>
                <LineChart data={data.trend} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
                  <XAxis dataKey="day" tick={{ fontSize: 10 }} />
                  <YAxis tick={{ fontSize: 11 }} domain={[0, 100]} />
                  <Tooltip />
                  <Line type="monotone" dataKey="taux_occupation_pct" name="Occupation" stroke="#7C3AED" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </Panel>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginTop: 16 }}>
            <Panel title="Temps d'attente / duree de prise en charge (min)">
              <ResponsiveContainer width="100%" height={220}>
                <LineChart data={data.trend} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
                  <XAxis dataKey="day" tick={{ fontSize: 10 }} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Line type="monotone" dataKey="temps_attente_moyen_min" name="Attente" stroke="#F59E0B" strokeWidth={2} dot={false} connectNulls />
                  <Line type="monotone" dataKey="duree_prise_en_charge_moyenne_min" name="Prise en charge" stroke="#3B6D11" strokeWidth={2} dot={false} connectNulls />
                </LineChart>
              </ResponsiveContainer>
            </Panel>
            <Panel title="Annulations / no-show par jour">
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={data.trend} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
                  <XAxis dataKey="day" tick={{ fontSize: 10 }} />
                  <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                  <Tooltip />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Bar dataKey="annulations" name="Annulations" fill="#94A3B8" radius={[3, 3, 0, 0]} />
                  <Bar dataKey="no_show" name="No-show" fill="#A32D2D" radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </Panel>
          </div>

          <div style={{ fontSize: 12, color: "#94A3B8", marginTop: 18, lineHeight: 1.6 }}>
            Le taux d'occupation est calcule sur une ressource partagee (pas encore par salle ou agent). Le no-show est marque manuellement par le personnel dans l'agenda (bouton "Absent"). La comparaison entre sites n'est pas encore disponible (multi-sites a venir).
          </div>
        </>
      )}
    </div>
  );
}
